import React, { useState, useEffect } from "react";
import { Grid, Text, InlineStack } from "@shopify/polaris";

import styles from "../../styles/layouts/Dashboard/dashboard.module.css";

import InfoCard from "../../components/Common/InfoCard.jsx";
import TipsCard from "../../components/Dashboard/ProductReviewCard.jsx";
import OrderTable from "../../components/Dashboard/OrderTable.jsx";
import { GetOrderOverview, GetOrders } from "../../services/order/index.js";
import useOrderStore from "../../store/order/index.js";
import EmptyState from "../../components/Common/EmptyState.jsx";
import useBundlesStore from "../../store/bundle/index.js";
import { GetBundles } from "../../services/bundle/index.js";

const merchantTips = [
  {
    tip: "Use Relevant Tags",
    description:
      "Add as many relevant tags as possible (e.g., ‘Festive’, ‘Birthday’, ‘Luxury’) to improve search results and help consumers find your bundles easily.",
  },
  {
    tip: "Write Detailed Descriptions",
    description:
      "Provide clear and detailed descriptions for your bundles to help customers understand what’s included and why it’s perfect for their needs.",
  },
  {
    tip: "Upload High-Quality Images",
    description:
      "Use crisp, high-resolution images to showcase your products. Better visuals lead to higher engagement and increased sales.",
  },
  {
    tip: "Give Innovative Names to Bundles",
    description:
      "Creative and appealing names (e.g., ‘Joyful Celebrations Hamper’) attract attention and make your bundles more memorable.",
  },
  {
    tip: "List All Products in the Bundle",
    description:
      "Always mention the complete list of products in a bundle to set clear expectations for customers.",
  },
  {
    tip: "Keep Stock Ready",
    description:
      "Ensure you have sufficient inventory of all products in your bundles to avoid delays or stockouts during peak sales.",
  },
  {
    tip: "Order Packaging in Advance",
    description:
      "Pre-order boxes and packaging materials to ensure you’re ready to ship during high-demand seasons.",
  },
  {
    tip: "Highlight Unique Selling Points",
    description:
      "Emphasize what makes your bundles special—exclusive items, limited editions, or discounts—to stand out in the marketplace.",
  },
  {
    tip: "Stay Seasonal and Relevant",
    description:
      "Create bundles tailored to trending occasions like weddings, festivals, or corporate events for maximum relevance.",
  },
  {
    tip: "Monitor Performance Regularly",
    description:
      "Track your bundles’ sales and customer feedback to optimize future offerings and adjust strategies as needed.",
  },
];

export default function Dashboard() {
  const { orders, setOrders } = useOrderStore();
  const { setBundles, bundles } = useBundlesStore();

  const [orderSummary, setOrderSummary] = useState([
    { label: "Total Revenue", value: "-" },
    { label: "Net Revenue", value: "-" },
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const [salesSummary, setSalesSummary] = useState([
    { label: "Pending", value: "-" },
    { label: "Shipped", value: "-" },
    { label: "Delivered", value: "-" },
  ]);
  const fetchBundles = async () => {
    if (!bundles.length) {
      const { data } = await GetBundles(currentPage);
      if (data?.result) {
        setBundles(data.result);
      }
    }
  };

  const capitalizeUnderscoreString = (str) => {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const fetchOrders = async () => {
    const { data } = await GetOrders(currentPage);
    if (data?.result?.length) {
      setOrders(data.result);
    }
  };
  const fetchOrdersOverview = async () => {
    const { data } = await GetOrderOverview();
    if (data.result?.salesSummary && data.result?.orderSummary) {
      setOrderSummary(
        Object.entries(data.result?.salesSummary).map(([key, value]) => {
          return {
            label: capitalizeUnderscoreString(key),
            value,
          };
        })
      );
      setSalesSummary(
        Object.entries(data.result?.orderSummary).map(([key, value]) => {
          return {
            label: capitalizeUnderscoreString(key),
            value,
          };
        })
      );
    }
  };
  useEffect(() => {
    fetchOrders();
    fetchOrdersOverview();
    fetchBundles();
  }, [currentPage]);
  return (
    <>
      {!bundles.length ? (
        <EmptyState
          heading={"Welcome to Giftclub"}
          text={"To get started, create a bundle"}
          btnName={"Add Bundle"}
          redirect={"/bundles/create"}
        />
      ) : (
        <>
          <Grid columns={{ xs: 1, sm: 1, md: 12, lg: 12, xl: 12 }}>
            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 8, lg: 8, xl: 8 }}>
              <InlineStack align={"space-between"} wrap={false}>
                <Text as="h3" variant="headingSm">
                  Overview
                </Text>
                {/* <Text as="h3" variant="bodyMd" tone="subdued">
              Today
            </Text> */}
              </InlineStack>
              <br />
              <Grid>
                {orderSummary.map((order) => {
                  return (
                    <InfoCard
                      columnSpan={{ xs: 4, sm: 4, md: 6, lg: 6, xl: 6 }}
                      label={order.label}
                      value={order.value}
                    />
                  );
                })}
              </Grid>
              <br />
              <InlineStack align={"space-between"} wrap={false}>
                <Text as="h3" variant="headingSm">
                  Orders Summary
                </Text>
                {/* <Text as="h3" variant="bodyMd" tone="subdued">
              Today
            </Text> */}
              </InlineStack>
              <br />
              <Grid>
                {salesSummary.map((order) => {
                  return (
                    <InfoCard
                      columnSpan={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}
                      label={order.label}
                      value={order.value}
                    />
                  );
                })}
              </Grid>
            </Grid.Cell>

            <Grid.Cell columnSpan={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
              <Text as="h3" variant="headingSm">
                Your Guide to GiftClub
              </Text>

              <br />
              <div className={styles["right-cell"]}>
                <Grid columns={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}>
                  {merchantTips.map((tip) => {
                    return (
                      <TipsCard text={tip.description} heading={tip.tip} />
                    );
                  })}
                </Grid>
              </div>
            </Grid.Cell>
          </Grid>
          <br />
          <Text as="h3" variant="headingSm">
            Overview
          </Text>
          <br />
          <OrderTable
            orders={orders}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
